"use client";

import {
  HighlightedText,
  type HighlightTone,
} from "@/components/tool-kit/highlighted-text";
import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  LABEL,
  TEXTAREA_PROSE,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { TEXT_ANONYMIZER_STRINGS } from "@/components/tools/text-anonymizer";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import type { AnonymizeResult } from "@/lib/tool-kit/qualitative-text/anonymize";
import {
  type AnonCategory,
  keyToCsv,
  type ListCategory,
} from "@/lib/tool-kit/qualitative-text/anonymize-names";

type Strings = typeof TEXT_ANONYMIZER_STRINGS;

const TONES: Record<AnonCategory, HighlightTone> = {
  person: "sky",
  place: "emerald",
  organisation: "violet",
  other: "violet",
  email: "rose",
  phone: "rose",
  url: "rose",
  handle: "rose",
  ip: "rose",
  postcode: "emerald",
  id: "rose",
  date: "amber",
};

const KEY_HEAD = ["category", "original", "pseudonym", "count"] as const;
const BOM = "﻿";
const ADD_TARGETS: readonly ListCategory[] = [
  "person",
  "place",
  "organisation",
];

export function AnonymizerResults({
  text,
  result,
  strings,
  onAddTerm,
}: {
  text: string;
  result: AnonymizeResult;
  strings: Strings;
  onAddTerm: (category: ListCategory, term: string) => void;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const total = result.replacements.length;
  const keyCsv = keyToCsv(result.key, KEY_HEAD);
  const matches = result.replacements.map((item) => ({
    start: item.start,
    end: item.end,
    category: item.category,
    title: `${item.original} → ${item.replacement}`,
  }));
  return (
    <section aria-live="polite" className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <Notice>{strings.reviewWarning}</Notice>
      <StatGrid>
        <StatCard emphasis label={strings.statReplaced} value={fmt(total)} />
        <StatCard
          label={strings.statDistinct}
          value={fmt(
            new Set(result.replacements.map((r) => r.replacement)).size
          )}
        />
        <StatCard
          label={strings.statCandidates}
          value={fmt(result.candidates.length)}
        />
      </StatGrid>
      <div className="space-y-2">
        <label className={LABEL} htmlFor="anon-output">
          {strings.outputLabel}
        </label>
        <textarea
          className={TEXTAREA_PROSE}
          id="anon-output"
          readOnly
          rows={10}
          value={result.output}
        />
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyOutput}
            text={result.output}
          />
          <button
            className={BUTTON_SECONDARY}
            onClick={() => downloadText(strings.outputFilename, result.output)}
            type="button"
          >
            {strings.downloadOutput}
          </button>
        </div>
      </div>
      <details className="rounded-lg border border-border bg-card p-3">
        <summary className={`${LABEL} cursor-pointer`}>
          {strings.showHighlights}
        </summary>
        <div className="mt-3 space-y-2">
          <p className={HINT}>{strings.highlightHint}</p>
          <HighlightedText
            label={strings.highlightLabel}
            matches={matches}
            text={text}
            tones={TONES}
          />
        </div>
      </details>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.keyHeading}</h3>
        {result.key.length === 0 ? (
          <Notice>{strings.keyEmpty}</Notice>
        ) : (
          <>
            <p className={HINT}>{strings.keyHint}</p>
            <DataTable
              head={[
                strings.colCategory,
                strings.colOriginal,
                strings.colPseudonym,
                strings.colCount,
              ]}
              rows={result.key.map((row) => [
                strings.categories[row.category],
                row.original,
                row.pseudonym,
                fmt(row.count),
              ])}
            />
            <div className="flex flex-wrap gap-3">
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  downloadText(
                    strings.keyFilename,
                    BOM + keyCsv,
                    "text/csv;charset=utf-8"
                  )
                }
                type="button"
              >
                {strings.downloadKey}
              </button>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyKey}
                text={keyCsv}
              />
            </div>
          </>
        )}
      </div>
      {result.ambiguous.length > 0 ? (
        <Notice>
          {t(strings.ambiguous, { names: result.ambiguous.join(", ") })}
        </Notice>
      ) : null}
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.candidatesHeading}</h3>
        <p className={HINT}>{strings.candidatesHint}</p>
        {result.candidates.length === 0 ? (
          <Notice>{strings.candidatesNone}</Notice>
        ) : (
          <ul className="space-y-2">
            {result.candidates.map((candidate) => (
              <li
                className="flex flex-wrap items-center gap-2 text-foreground text-sm"
                key={candidate.text}
              >
                <span className="font-medium">{candidate.text}</span>
                <span className="text-muted-foreground">
                  {tPlural(strings.candidateCount, locale, candidate.count, {
                    count: fmt(candidate.count),
                  })}
                </span>
                {ADD_TARGETS.map((category) => (
                  <button
                    className={BUTTON_SMALL}
                    key={category}
                    onClick={() => onAddTerm(category, candidate.text)}
                    type="button"
                  >
                    {t(strings.addTo, { list: strings.listTitles[category] })}
                  </button>
                ))}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
